import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  AlertTriangle,
  X,
  Plus,
  Video,
  Users,
  Settings,
  Mic,
  Shield,
  Lock,
  UserCheck,
} from "lucide-react";
import RoomCreateModal from "./RoomCreateModal";
import EditRoomModal from "./EditRoomModal";
import { toast } from "react-toastify";
import {
  getMeetingRooms,
  createMeetingRoom,
  deleteMeetingRoom,
  updateMeetingRoom,
  getActiveRoomStatus,
} from "../../../../services/roomServices";
import { useSiteContext } from "../../../../context/SiteContext";
const Rooms = () => {
  const { user } = useSiteContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rooms, setRooms] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRooms, setTotalRooms] = useState(0);
  const [maxRooms, setMaxRooms] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [activeChannels, setActiveChannels] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getRooms();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    fetchActiveStatus();
    const interval = setInterval(fetchActiveStatus, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchActiveStatus = async () => {
    try {
      const res = await getActiveRoomStatus();
      setActiveChannels(res.activeChannels || {});
    } catch (err) {
      console.warn("Failed to fetch active room status:", err);
    }
  };


  const getRoomType = (roomType) => {
    return String(roomType).toLowerCase() === "conferencing"
      ? "Meeting"
      : roomType;
  };

  const getRooms = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getMeetingRooms(currentPage, rowsPerPage);
      const allRooms = response.data || [];
      // Sort: owned rooms first, co-hosted rooms at the bottom
      allRooms.sort((a, b) => {
        const aOwned = a.roomCreatedBy === user?.id ? 0 : 1;
        const bOwned = b.roomCreatedBy === user?.id ? 0 : 1;
        return aOwned - bOwned;
      });
      setRooms(allRooms);
      setTotalRooms(response.totalOwnerRooms || 0);
      setMaxRooms(response.maxRooms || (user?.plan === "Business" ? 5 : 1));
      setTotalPages(response.pagination.totalPages || 1);
    } catch (err) {
      console.log(err);
      setError("Error fetching rooms");
      setRooms([]);
      setTotalRooms(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const sanitizeForUrl = (value) => (value || "").replace(/\s+/g, "-");

  const getPermalink = (room) => {
    const p = room.roomPermalink;
    if (!p || p === "false" || p === "true") {
      console.warn('[Rooms] Room missing valid permalink, falling back to roomName:', room.id, room.roomName);
      return room.roomName;
    }
    return p;
  };

  const getRoomDomain = () => {
    const wlDomain = user?.whitelabelDomain;
    return wlDomain ? `https://${wlDomain}` : window.location.origin;
  };

  const handleCopyRoom = (roomName, room) => {
    // Handle copy room functionality
    console.log("Copy room:", roomName);
    navigator.clipboard.writeText(`${getRoomDomain()}/${sanitizeForUrl(getPermalink(room))}`);
    toast.success("Room link copied to clipboard");
  };

  const handleLaunchRoom = (room) => {
    window.open(`${getRoomDomain()}/${sanitizeForUrl(getPermalink(room))}`, "_blank");
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setShowEditModal(true);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await deleteMeetingRoom(roomToDelete.id);
      getRooms(); // Refresh the rooms list
      setShowDeleteModal(false);
      setRoomToDelete(null);
      toast.success("Room deleted successfully");
    } catch (err) {
      console.error("Error deleting room:", err);
      toast.error("Error deleting room. Please try again.");
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteModal(false);
    setRoomToDelete(null);
  };

  const handleUpdateRoom = (roomData) => {
    console.log("Updating room with data:", roomData);
    updateMeetingRoom({
      ...roomData,
      _id: editingRoom._id,
      room: editingRoom.room,
    })
      .then((response) => {
        console.log(response);
        getRooms();
        setShowEditModal(false);
        setEditingRoom(null);
      })
      .catch((err) => {
        console.error("Error updating room:", err);
        alert("Error updating room. Please try again.");
      });
  };

  const handleCreateRoom = (roomData) => {
    console.log("Creating room with data:", roomData);
    // Add your API call here to create the room
    // After successful creation, refresh the rooms list
    createMeetingRoom({
      ...roomData,
    }).then((response) => {
      console.log(response);
      getRooms();
      setShowCreateModal(false);
    });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen text-white p-3 md:p-6 flex items-center justify-center"
        style={{ backgroundColor: "#001233" }}
      >
        <div className="text-base md:text-xl">Loading rooms...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-3 md:p-6" style={{ backgroundColor: "#001233" }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              Rooms
            </h1>
            <p className="text-sm md:text-base text-gray-400 mt-1">
              Manage your virtual spaces • {totalRooms} of {maxRooms} rooms used
            </p>
          </div>
          {user &&
            user.role !== "member" &&
            totalRooms < maxRooms && (
              <button
                onClick={() => navigate("/dashboard/add-new-room")}
                className="px-4 md:px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg transition-all font-semibold hover:shadow-lg hover:shadow-purple-500/25 flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <Plus className="h-4 w-4 md:h-5 md:w-5" />
                <span>Create Room</span>
              </button>
          )}
        </div>

        {/* Room Usage Progress */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm md:text-base font-medium text-white">Room Usage</h3>
            <span className="text-xs md:text-sm text-gray-400">
              {totalRooms}/{maxRooms}
            </span>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] transition-all duration-500"
              style={{ width: `${maxRooms > 0 ? (totalRooms / maxRooms) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm md:text-base">
            {error}
          </div>
        )}

        {/* Empty State */}
        {rooms.length === 0 && !loading && !error && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
            <Video className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No rooms yet</h3>
            <p className="text-gray-400 mb-6">Create your first room to get started</p>
            {user &&
              user.role !== "member" &&
              ((user.plan === "Business" && totalRooms < 5) ||
                (user.plan === "Basic" && totalRooms < 1)) && (
                <button
                  onClick={() => navigate("/dashboard/add-new-room")}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg transition-all font-semibold hover:shadow-lg hover:shadow-purple-500/25 inline-flex items-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>Create Room</span>
                </button>
            )}
          </div>
        )}

        {/* Rooms Grid */}
        {rooms.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5 md:p-6 hover:border-[#6a1fbf]/50 hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300"
              >
                {/* Room Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex flex-col min-w-0 gap-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="text-lg md:text-xl font-bold text-white truncate">
                        {room.roomName}
                        {room.roomCreatedBy !== user?.id &&
                          !(
                            user?.role === "member" &&
                            Array.isArray(room.roomAssignedMemberId) &&
                            room.roomAssignedMemberId.map(String).includes(String(user?.id))
                          ) && (
                            <span className="text-sm font-medium text-purple-300 ml-1">(as Co-Host)</span>
                          )}
                      </h3>
                      {activeChannels[room.roomId] > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex-shrink-0">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                          </span>
                          Live
                        </span>
                      )}
                      {room.privacy === true && (
                        <span
                          className="group relative inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-500/15 text-green-400 border border-green-500/40 flex-shrink-0 cursor-help"
                          aria-label="Privacy Mode"
                          data-testid={`room-privacy-lock-${room.id}`}
                        >
                          <Lock className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Privacy</span>
                          <span
                            role="tooltip"
                            className="invisible group-hover:visible absolute top-full left-0 mt-2 w-72 px-3 py-2 rounded-lg bg-[#0D1117] border border-white/15 text-[11px] font-normal text-gray-200 shadow-xl z-50"
                          >
                            Privacy mode is enabled. Recording, screenshots, and screen capture are disabled to protect this meeting.
                          </span>
                        </span>
                      )}
                    </div>
                    {user?.role !== "member" && Array.isArray(room.assignedMembers) && room.assignedMembers.length > 0 && (
                      <div
                        className="flex items-center gap-1 text-[11px] text-amber-300/90 truncate"
                        title={room.assignedMembers.map((m) => m.email || m.name).join(", ")}
                      >
                        <UserCheck className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">
                          Assigned to {room.assignedMembers.map((m) => m.name || m.email).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                  {(room.roomCreatedBy === user?.id ||
                    (user?.role === "member" &&
                      Array.isArray(room.roomAssignedMemberId) &&
                      room.roomAssignedMemberId.map(String).includes(String(user?.id)))) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditRoom(room); }}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Edit Room"
                    >
                      <Settings className="h-5 w-5 text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Room URL */}
                <div className="flex items-center gap-2 mb-4 p-2.5 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-sm text-gray-400 truncate flex-1">
                    {user?.whitelabelDomain || window.location.host}/{sanitizeForUrl(getPermalink(room))}
                  </span>
                  <button
                    onClick={() => handleCopyRoom(room.roomName, room)}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors flex-shrink-0"
                    title="Copy link"
                  >
                    <Copy className="h-4 w-4 text-gray-400" />
                  </button>
                </div>

                {/* Room Stats */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <Video className="h-4 w-4" />
                    <span>0 sessions</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4" />
                    <span>0 participants</span>
                  </div>
                </div>

                {/* Co-hosts & Presenters */}
                {((room.coHosts && room.coHosts.length > 0) || (room.speakers && room.speakers.length > 0)) && (
                  <div className="mb-4 space-y-2">
                    {room.coHosts && room.coHosts.length > 0 && (
                      <div className="flex items-start gap-2 text-sm">
                        <Shield className="h-3.5 w-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {room.coHosts.map((email, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20"
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {room.speakers && room.speakers.length > 0 && (
                      <div className="flex items-start gap-2 text-sm">
                        <Mic className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {room.speakers.map((email, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Launch Button */}
                <button
                  onClick={() => handleLaunchRoom(room)}
                  className="w-full py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-all flex items-center justify-center gap-2 font-medium border border-white/10 hover:border-white/20"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Launch</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {rooms.length > 0 && totalPages > 1 && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center space-x-2 text-xs md:text-sm text-white">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) =>
                    handleRowsPerPageChange(Number(e.target.value))
                  }
                  className="bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#6a1fbf] hover:border-white/30 transition-colors"
                >
                  <option value={10} className="bg-[#1a1a2e] text-white">
                    10
                  </option>
                  <option value={25} className="bg-[#1a1a2e] text-white">
                    25
                  </option>
                  <option value={50} className="bg-[#1a1a2e] text-white">
                    50
                  </option>
                </select>
              </div>
              <div className="flex items-center space-x-2 md:space-x-4">
                <span className="text-xs md:text-sm text-white">
                  {currentPage} of {totalPages}
                </span>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronsLeft className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronsRight className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Room Create Modal */}
      <RoomCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateRoom={handleCreateRoom}
      />

      {/* Room Edit Modal */}
      <EditRoomModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingRoom(null);
        }}
        room={editingRoom}
        onUpdate={() => {
          getRooms();
          setShowEditModal(false);
          setEditingRoom(null);
        }}
        onDelete={editingRoom && editingRoom.roomCreatedBy === user.id ? () => {
          setShowEditModal(false);
          setRoomToDelete(editingRoom);
          setEditingRoom(null);
          setShowDeleteModal(true);
        } : null}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-[#0000] bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl md:rounded-2xl shadow-2xl w-full max-w-md border border-gray-700 transform transition-all duration-300 ease-out">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-700 bg-gradient-to-r from-red-900/20 to-orange-900/20">
              <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-500/20 rounded-lg md:rounded-xl flex items-center justify-center border border-red-500/30 flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-red-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg md:text-xl font-bold text-white truncate">
                    Delete Room
                  </h2>
                  <p className="text-gray-400 text-xs md:text-sm">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={cancelDeleteRoom}
                className="p-2 hover:bg-gray-700/50 rounded-full transition-all duration-200 hover:scale-110 group flex-shrink-0 ml-2"
              >
                <X className="h-5 w-5 text-gray-300 group-hover:text-white transition-colors" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 md:p-6">
              <div className="mb-4 md:mb-6">
                <p className="text-gray-300 text-xs md:text-sm mb-3 md:mb-4">
                  Are you sure you want to delete the room{" "}
                  <span className="font-semibold text-white">
                    "{roomToDelete?.roomName}"
                  </span>
                  ?
                </p>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 md:p-4">
                  <div className="flex items-start space-x-2 md:space-x-3">
                    <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-red-300 font-medium text-xs md:text-sm mb-1">
                        Warning
                      </h4>
                      <p className="text-red-200 text-xs">
                        This will permanently delete the room and all its data.
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-0">
                <button
                  onClick={cancelDeleteRoom}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-gray-300 bg-gray-700/50 border border-gray-600 rounded-lg hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteRoom}
                  className="w-full sm:w-auto px-4 md:px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 border border-transparent rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 shadow-lg shadow-red-500/25 transition-all duration-200 hover:scale-105 hover:shadow-red-500/40 flex items-center justify-center"
                >
                  <Trash2 className="h-4 w-4 inline mr-2" />
                  Delete Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rooms;
